<template src="./question.html"></template>

<script lang="ts">
import { defineAsyncComponent, nextTick } from 'vue'
import domUtils from 'util/dom-utils'
import { getOfflineOrUnexpectedMsg } from 'services/msg'
import globalConfig from 'init/global.config'
import globalStore from 'init/global-store'
import Byline from 'objects/byline/Byline.vue'
import Answer from 'components/answers/Answer.vue'
import ProductAbout from 'components/product-about/ProductAbout.vue'
import { RestError } from 'services/api-service'
import { getSessionVarInt, setSessionVar, removeSessionVar, QUESTION_VOTED_TYPE }
  from 'util/session-storage'
import voteQuestionService from 'services/api/vote-question'
import trackingService from 'services/tracking-service'


export default {
  data() {
    return {
      bestAnswerStyles: {
        overflow: 'hidden'
      },
      globalConfig,
      globalStoreState: globalStore.state,
      showAnswerForm: false,
      showAnswers: false,
      wasFlagged: false,
      wasVotedOn: false,
      // userSessionVote is used to track when a vote, up or down has been cast by the user
      userSessionVote: this.question.upVotes,
      voteBeingProcessed: false
    }
  },
  components: {
    'tt-answer': Answer,
    'tt-answer-form': defineAsyncComponent(
      // eslint-disable-next-line max-len
      () => import(/* webpackChunkName: 'answer-reply-form' */ 'components/answers/AnswerReplyForm.vue')
    ),
    'tt-byline': Byline,
    'tt-product-about': ProductAbout
  },
  props: {
    // to originally display as open
    displayExpanded: Boolean,
    // this is the heading level for the question text of this component
    // this is the highest level for this component, the exception being
    // the solo modal title, which is the modal heading level
    // should be 1 less than the widget header
    headingLevel: { type: Number, default: undefined },
    isRelated: Boolean,
    originSiteKey: String,
    question: { type: Object },
    showActions: {
      type: Boolean,
      default: true
    },
    showBestAnswer: Boolean
  },
  emits: ['showError'],
  inject: {
    soloQuestionCtx: {
      from: 'soloQuestionCtx',
      default: {}
    }
  },
  provide() {
    return {
      questionId: this.question.id
    }
  },
  computed: {
    addAnswerDisabled() {
      return this.userAskedThisQuestion
    },
    bestAnswer() {
      const answers = this.question.answers
      return this.showBestAnswer && !this.showAnswers && answers.length > 0 ? answers[0] : null
    },
    canVote() {
      return !this.userAskedThisQuestion && !this.voteBeingProcessed
    },
    controlsId() {
      // id for aria-controls on add answer button.  id is on answer form
      return `tt-answer-form-${this.question.id}`
    },
    expanderData() {
      const numAnswers = this.question.answers.length
      if (numAnswers === 0 || (numAnswers === 1 && this.showBestAnswer)) {
        return null
      }
      if (this.showAnswers) {
        return {
          expanded: 'true',
          icon: 'options-collapse',
          text: this.$msg('question.hideAnswers', [], this.question.answers.length)
        }
      }
      return {
        expanded: 'false',
        icon: 'options-expand',
        text: this.$msg('question.showAnswers',
          [this.question.answers.length], this.question.answers.length)
      }
    },
    hideAddAnswer() {
      return !!this.soloQuestionCtx.value?.isSolo && this.question.answers.length === 0
    },
    hideAnswerFormCancel() {
      return this.soloQuestionCtx.value?.isSolo && this.question.answers.length === 0
    },
    userAskedThisQuestion() {
      // check for question.user, may be anonymous
      return this.globalStoreState.user && this.globalStoreState.user.id && this.question.user &&
        this.question.user.id && this.globalStoreState.user.id === this.question.user.id
    },
    voteCount() {
      return this.userSessionVote
    },
    voteIconName() {
      return this.wasVotedOn ? 'vote-on' : 'vote-off'
    }
  },
  methods: {
    closeForm() {
      this.showAnswerForm = false
      // TT-11853 - should focus back on "Add Answer" after closing form
      domUtils.safeFocusRef(this.$refs, 'addAnswer')
    },
    toggleShowHideAnswers() {
      if (this.$refs.bestAnswer) {
        // if there is a best answer, add animation to scroll it up when showing the answer list
        // and scroll it down when hiding the answer list
        // Idea of how to accomplish taken from js solution proposed here:
        // https://css-tricks.com/using-css-transitions-auto-dimensions/
        const elementTransition = 'height 0.5s ease-out'
        const bestAnswerEl = this.$refs.bestAnswer.$el
        // scrollHeight is the height it is naturally
        const scrollHeight = `${bestAnswerEl.scrollHeight}px`
        // first need to clear out the transition so it will take affect AFTER the height change
        this.bestAnswerStyles.transition = ''
        requestAnimationFrame(() => {
          // set the height to what it is currently, then set back the transition so it will
          // take effect when the height is change below
          this.bestAnswerStyles.height = this.showAnswers ? '0px' : scrollHeight
          this.bestAnswerStyles.transition = elementTransition

          // on the next frame (as soon as the previous style change has taken effect),
          // have the element transition to the desired height
          requestAnimationFrame(() => {
            this.bestAnswerStyles.height = this.showAnswers ? scrollHeight : '0px'
            // very brief delay before showing/hiding the answer list
            requestAnimationFrame(() => {
              this.showAnswers = !this.showAnswers
              this.fireShowHideEvent(this.showAnswers)
            })
          })
        })
      } else {
        this.showAnswers = !this.showAnswers
        this.fireShowHideEvent(this.showAnswers)
      }
    },
    fireShowHideEvent(showHide) {
      trackingService.queueEvent('qa', showHide ? 'show-answers' : 'hide-answers',
        this.question.catItem.sku, {
          qid: this.question.id
        })
    },
    vote(e) {
      // have to get reference to currentTarget right away as it ends up null later on
      const voteBtn = e.currentTarget
      this.voteBeingProcessed = true
      const up = this.wasVotedOn ? -1 : 1

      return voteQuestionService.voteQuestion({
        id: this.question.id,
        originSiteKey: this.soloQuestionCtx.value?.originSiteKey || '',
        sku: this.question.catItem.sku
      }, {
        up
      }).then(() => {
        this.wasVotedOn = up === 1 ? true : false
        this.userSessionVote += (this.wasVotedOn ? 1 : -1)
        if (this.wasVotedOn) {
          setSessionVar(QUESTION_VOTED_TYPE, this.question.id, this.userSessionVote)
        } else {
          removeSessionVar(QUESTION_VOTED_TYPE, this.question.id)
        }
        this.voteBeingProcessed = false
        nextTick(() => voteBtn.focus())
        trackingService.queueEvent('qa', 'vote', this.question.catItem.sku, {
          qid: this.question.id, vote: up
        })
      }).catch((e: RestError) => {
        this.voteBeingProcessed = false
        this.$emit('showError', getOfflineOrUnexpectedMsg(e))
      })
    }
  },
  created() {
    this.showAnswers = this.displayExpanded && this.question.answers.length > 0
    this.showAnswerForm = !!this.soloQuestionCtx.value?.openAnswerForm
    const storedSessionValue = getSessionVarInt(QUESTION_VOTED_TYPE, this.question.id)
    if (storedSessionValue) {
      this.wasVotedOn = true
      this.userSessionVote = storedSessionValue
    }
  }
}
</script>

<style lang="scss" src="./question.scss"></style>
